// react
import React, { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

// material ui
import { Box, IconButton, Tooltip } from '@mui/material';

// api types
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// relative
import EnergyKpiCard from './EnergyKpiCard.tsx';
import {
  PortfolioKpiSparkline,
  PortfolioKpiSparklineDialog,
} from './PortfolioKpiSparkline.tsx';

interface PortfolioKpiCardProps {
  title: string;
  icon: React.ReactElement;
  color: string;
  path?: string;
  onClick: () => void;
  totalCurrentYear: number | undefined;
  totalLastYear: number | undefined;
  unit: string;
  enabled: boolean | undefined;
  period?: string;
  trendTooltip?: string;
  summedData?: TimeSeriesDataPoint[];
}

const PortfolioKpiCard = ({
  title,
  icon,
  color,
  path,
  onClick,
  totalCurrentYear,
  totalLastYear,
  unit,
  enabled,
  period,
  trendTooltip,
  summedData,
}: PortfolioKpiCardProps) => {
  const { t } = useTranslation([
    'energy-analysis',
    'portfolio-analysis_dashboard',
  ]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canOpenDialog = Array.isArray(summedData) && summedData.length > 0;

  const yoyPct =
    totalLastYear != null && totalLastYear > 0 && totalCurrentYear != null
      ? ((totalCurrentYear - totalLastYear) / totalLastYear) * 100
      : undefined;

  const actions = enabled ? (
    <Box display="flex" alignItems="center" gap={0.5}>
      {canOpenDialog ? (
        <Tooltip
          title={t('actions.showChart', {
            ns: 'portfolio-analysis_dashboard',
          })}
        >
          <IconButton
            size="small"
            aria-label={t('actions.showChart', {
              ns: 'portfolio-analysis_dashboard',
            })}
            onClick={() => setIsDialogOpen(true)}
          >
            <OpenInFullRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {path ? (
        <Tooltip
          title={t('actions.openDetails', {
            ns: 'portfolio-analysis_dashboard',
          })}
        >
          <IconButton
            size="small"
            aria-label={t('actions.openDetails', {
              ns: 'portfolio-analysis_dashboard',
            })}
            onClick={onClick}
          >
            <LaunchRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  ) : null;

  return (
    <>
      <EnergyKpiCard
        title={title}
        icon={icon}
        color={color}
        value={totalCurrentYear}
        unit={unit}
        enabled={enabled}
        actions={actions}
        period={period ?? t('portfolio.kpi_cards.period_last_12_months')}
        trend={yoyPct}
        trendTooltip={trendTooltip ?? t('portfolio.kpi_cards.trend_tooltip')}
        unavailableText={t('portfolio.status.not_subscribed')}
        noDataText={t('portfolio.kpi_cards.no_data')}
      >
        {canOpenDialog ? (
          <PortfolioKpiSparkline
            dataPoints={summedData}
            unit={unit}
            color={color}
          />
        ) : null}
      </EnergyKpiCard>
      <PortfolioKpiSparklineDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        dataPoints={summedData}
        unit={unit}
        color={color}
        label={title}
      />
    </>
  );
};

export default PortfolioKpiCard;
